import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, EmptyState, Select, Spinner, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type CoverCandidate, type CoverOffer } from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { Pill } from '../components/Pill';

/**
 * Cover queue — deliveries a Runner released by booking time off.
 *
 * 🛑 THIS SCREEN WATCHES THE MARKETPLACE; IT DOES NOT DRIVE IT. Rod ruled a
 * fully automated offer-to-eligible-Runners marketplace on 2026-09-12: the
 * offer fans out the moment a Runner blocks days, the first Runner to accept
 * takes it, and an unclaimed offer expires on a ten-minute sweep. The Figma
 * frames for this screen were drawn BEFORE that ruling and say "Nothing here is
 * automatic yet. A person offers the run" — they are out of date, and this
 * comment exists so nobody rebuilds them.
 *
 * What a person is here for is the part automation is bad at:
 *
 *  - noticing that a zone had nobody to offer it to at all;
 *  - WIDENING past a zone boundary, which is only ever a proxy for "can get
 *    there" — never past the window, because somebody who cannot be there at
 *    4 PM is not cover, they are a second cancellation;
 *  - naming a Runner ops knows would say yes.
 *
 * 🔒 Offering PUSHES a Runner. It never assigns them. They accept in their own
 * app and first accept still wins, whoever offered it — putting somebody on a
 * doorstep who never agreed to be there is the one thing this screen must not
 * be able to do.
 */
const GRID = '1.3fr 1.1fr 0.8fr 1fr 0.9fr 0.7fr';

type Filter = 'open' | 'covered' | 'expired' | 'all';

function clock(hhmm: string): string {
  const h = Number(hhmm.slice(0, 2));
  const m = hhmm.slice(3, 5);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === '00' ? `${h12} ${suffix}` : `${h12}:${m} ${suffix}`;
}
function windowLabel(a: string, b: string): string {
  const start = clock(a);
  const end = clock(b);
  return start.slice(-2) === end.slice(-2) ? `${start.slice(0, -3)}–${end}` : `${start}–${end}`;
}
function dayLabel(ymd: string): string {
  return new Date(`${ymd}T00:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * 🪤 Minutes matter here. An offer for tomorrow evening can have two hours on
 * it, and "0h" is how a person scrolls past the one that needed them.
 */
function closesIn(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'closed';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

const STATUS_PILL: Record<string, { label: string; fg: string; bg: string }> = {
  open: { label: 'NEEDS COVER', fg: '#7A4A08', bg: '#FDF3E1' },
  covered: { label: 'COVERED', fg: '#15803d', bg: '#dcfce7' },
  expired: { label: 'NO COVER', fg: '#b42318', bg: '#fee4e2' },
  withdrawn: { label: 'WITHDRAWN', fg: '#64748b', bg: '#f1f5f9' },
};

export default function CoverQueue() {
  const caps = useCapabilities();
  const canView = caps.has('runner:read');
  const canOffer = caps.has('runner:cover:manage');
  const [filter, setFilter] = useState<Filter>('open');
  const [offers, setOffers] = useState<CoverOffer[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<CoverCandidate[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listCoverOffers(filter);
      setOffers(res.offers);
      setCounts(res.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the cover queue');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  async function openCandidates(offerId: string) {
    if (expanded === offerId) {
      setExpanded(null);
      return;
    }
    setExpanded(offerId);
    setCandidates(null);
    try {
      const res = await adminApiClient.getCoverCandidates(offerId);
      setCandidates(res.candidates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Runners for this run');
    }
  }

  async function offerTo(offerId: string, c: CoverCandidate) {
    setBusy(c.runnerId);
    setNote(null);
    try {
      await adminApiClient.offerCoverToRunner(offerId, c.runnerId);
      setNote(`Offered to ${c.name ?? 'that Runner'}. They accept in their own app — first to accept gets it.`);
      const res = await adminApiClient.getCoverCandidates(offerId);
      setCandidates(res.candidates);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That offer did not go out');
    } finally {
      setBusy(null);
    }
  }

  if (caps.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!canView) return <Alert severity="error">You don’t have permission to view the cover queue.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Cover queue</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Deliveries a Runner released by booking time off. The household has already been emailed and the run was
        offered automatically to every Runner whose window covers it. This is where a person widens the search or
        names someone.
        {counts.open ? ` ${counts.open} still open.` : ''}
      </Typography>

      {!canOffer && (
        <Box sx={{ mb: 2 }}><Alert severity="info">View-only access — you can read the queue but can’t offer a run.</Alert></Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Filter by status" label="Status" native size="small" sx={{ width: 220 }} value={filter}
          onChange={(_e, v) => setFilter(v as Filter)}
          options={[
            { value: 'open', label: `Needs cover (${counts.open ?? 0})` },
            { value: 'covered', label: `Covered (${counts.covered ?? 0})` },
            { value: 'expired', label: `Nobody covered (${counts.expired ?? 0})` },
            { value: 'all', label: 'All' },
          ]}
        />
      </Box>

      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {note && <Box sx={{ mb: 2 }}><Alert severity="success">{note}</Alert></Box>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>
      ) : offers.length === 0 ? (
        <EmptyState
          icon="onlineordering"
          title={filter === 'open' ? 'Nothing needs cover' : 'Nothing here'}
          body={filter === 'open' ? 'No delivery is waiting for a Runner right now.' : 'No released run matches this filter.'}
        />
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {['Delivery', 'Released by', 'Orders', 'Offered · passed · no answer', 'Status', ''].map((h, i) => (
              <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>{h.toUpperCase()}</Typography>
            ))}
          </Box>

          {offers.map((o, idx) => {
            const pill = STATUS_PILL[o.status] ?? STATUS_PILL.withdrawn;
            return (
              <Box key={o.id} sx={{ borderBottom: idx < offers.length - 1 ? '1px solid #eef2f6' : 'none' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.5, alignItems: 'center', '&:hover': { backgroundColor: '#fafbfc' } }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {dayLabel(o.runDate)} · {windowLabel(o.deliveryWindowStart, o.deliveryWindowEnd)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.zone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#1f2937' }}>{o.releasedBy.name ?? '—'}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.releasedBy.glpdId ?? '—'}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600 }}>
                    {o.orderCount} {o.orderCount === 1 ? 'order' : 'orders'}
                  </Typography>
                  <Box>
                    {/*
                      🛑 "0 offered" is the row that matters most on this screen:
                      the zone had nobody, so nothing is coming and only a person
                      widening it will change that.
                    */}
                    <Typography variant="body2" sx={{ color: o.offered === 0 ? '#b42318' : '#1f2937', fontWeight: o.offered === 0 ? 700 : 400 }}>
                      {o.offered === 0 ? 'Nobody was eligible' : `${o.offered} · ${o.passed} · ${o.noAnswer}`}
                    </Typography>
                    {o.status === 'open' && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>closes in {closesIn(o.expiresAt)}</Typography>
                    )}
                    {o.acceptedBy && (
                      <Typography variant="caption" sx={{ color: '#15803d' }}>{o.acceptedBy.name} took it</Typography>
                    )}
                  </Box>
                  <Box><Pill label={pill.label} fg={pill.fg} bg={pill.bg} /></Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Button
                      ariaLabel={`See Runners for ${dayLabel(o.runDate)}`}
                      buttonText={expanded === o.id ? 'Hide' : 'See Runners'}
                      variant="outlined"
                      size="small"
                      sx={NEUTRAL_BTN}
                      onClick={() => void openCandidates(o.id)}
                    />
                  </Box>
                </Box>

                {expanded === o.id && (
                  <Box sx={{ px: 2, pb: 2, backgroundColor: '#fafbfc' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 0.4 }}>
                      RUNNERS WHO CAN TAKE THIS
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                      Everyone in the metro with a window covering that day and time. Capacity is never a filter —
                      covering does not count against a Runner’s limits.
                    </Typography>
                    {candidates === null ? (
                      <Spinner color="brand" />
                    ) : candidates.length === 0 ? (
                      <Alert severity="warning">
                        Nobody in this metro has a window covering that day and time. Widening cannot help — the
                        household needs telling.
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'grid', gap: 1 }}>
                        {candidates.map((c) => (
                          <Box
                            key={c.runnerId}
                            sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                              flexWrap: 'wrap', border: '1px solid #e2e8f0', borderRadius: 1.5,
                              px: 1.5, py: 1, backgroundColor: '#fff',
                            }}
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: '#1f2937' }}>{c.name ?? c.runnerId}</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {c.glpdId ?? '—'} · {c.zones.join(', ')}
                                {c.inZone ? '' : ' · outside the run’s zone'}
                              </Typography>
                              {c.declinedAt && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                                  Passed. Passing is never held against a Runner.
                                </Typography>
                              )}
                              {c.offeredAt && !c.declinedAt && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                                  Offered already. No answer yet.
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {!c.inZone && <Pill label="WIDEN" fg="#7A4A08" bg="#FDF3E1" />}
                              <Button
                                ariaLabel={`Offer this run to ${c.name ?? 'this Runner'}`}
                                buttonText={c.offeredAt ? 'Offer again' : 'Offer this run'}
                                variant="outlined"
                                size="small"
                                sx={NEUTRAL_BTN}
                                disabled={!canOffer || busy !== null || o.status !== 'open'}
                                onClick={() => void offerTo(o.id, c)}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2 }}>
        Offering pushes and emails the Runner. They accept in their own app and the first to accept gets it,
        whoever offered it. If nobody covers it the orders are cancelled before the day and the hold was already
        released when the Runner blocked it — nobody is charged.
      </Typography>
    </Box>
  );
}
